import React, { useMemo } from "react";
import { MessageBanner } from "@folio/stripes/components";
import { useIntl } from "react-intl";
import { formatVolumeRange } from "../../utils/freedPeriodChoice";
import { describeVolume, formatVolumeAnchor, formatVolumeDate, } from "../../utils/volumeWindow";
import css from "./VolumeWindowPreview.module.css";
var MESSAGE_PREFIX = "ui-br-legaldeposits.periodicals.volume.window";
var EngagedMark = function (_a) {
    var engaged = _a.engaged;
    var formatMessage = useIntl().formatMessage;
    if (!engaged)
        return null;
    return (React.createElement("strong", { className: css.engagedMark }, formatMessage({ id: "".concat(MESSAGE_PREFIX, ".engagedMark") })));
};
var IssueList = function (_a) {
    var headingId = _a.headingId, issues = _a.issues, blocking = _a.blocking;
    var formatMessage = useIntl().formatMessage;
    if (issues.length === 0)
        return null;
    return (React.createElement("div", { className: blocking ? css.blockingSection : css.section },
        React.createElement("strong", null, formatMessage({ id: headingId }, { count: issues.length })),
        React.createElement("ul", { className: css.list }, issues.map(function (issue) {
            var _a;
            return (React.createElement("li", { key: issue.issueId },
                formatMessage({
                    id: issue.toVolumeLabel
                        ? "".concat(MESSAGE_PREFIX, ".issueMove")
                        : "".concat(MESSAGE_PREFIX, ".issueOrphan"),
                }, {
                    issue: issue.label,
                    date: issue.expectedDate
                        ? formatVolumeDate(issue.expectedDate)
                        : "—",
                    from: issue.fromVolumeLabel,
                    to: (_a = issue.toVolumeLabel) !== null && _a !== void 0 ? _a : "",
                }),
                React.createElement(EngagedMark, { engaged: issue.engaged })));
        }))));
};
var RenumberedList = function (_a) {
    var issues = _a.issues;
    var formatMessage = useIntl().formatMessage;
    if (issues.length === 0)
        return null;
    return (React.createElement("div", { className: css.section },
        React.createElement("strong", null, formatMessage({ id: "".concat(MESSAGE_PREFIX, ".renumbered") }, { count: issues.length })),
        React.createElement("ul", { className: css.list }, issues.map(function (issue) { return (React.createElement("li", { key: issue.issueId },
            formatMessage({ id: "".concat(MESSAGE_PREFIX, ".issueRenumbered") }, {
                issue: issue.label,
                newLabel: issue.newLabel,
                volume: issue.volumeLabel,
            }),
            React.createElement(EngagedMark, { engaged: issue.engaged }))); }))));
};
var FilledList = function (_a) {
    var issues = _a.issues;
    var formatMessage = useIntl().formatMessage;
    if (issues.length === 0)
        return null;
    return (React.createElement("div", { className: css.section },
        React.createElement("strong", null, formatMessage({ id: "".concat(MESSAGE_PREFIX, ".filled") }, { count: issues.length })),
        React.createElement("ul", { className: css.list }, issues.map(function (issue) { return (React.createElement("li", { key: "".concat(issue.volumeLabel, ":").concat(issue.issueNumber) }, formatMessage({ id: "".concat(MESSAGE_PREFIX, ".issueFilled") }, {
            issue: issue.label,
            date: formatVolumeDate(issue.expectedDate),
            volume: issue.volumeLabel,
        }))); }))));
};
var AfterTable = function (_a) {
    var rows = _a.rows, offAnchorLabels = _a.offAnchorLabels;
    var formatMessage = useIntl().formatMessage;
    return (React.createElement("div", { className: css.section },
        React.createElement("strong", null, formatMessage({ id: "".concat(MESSAGE_PREFIX, ".after.heading") })),
        React.createElement("table", { className: css.afterTable },
            React.createElement("thead", null,
                React.createElement("tr", null,
                    React.createElement("th", null, formatMessage({ id: "".concat(MESSAGE_PREFIX, ".after.volume") })),
                    React.createElement("th", null, formatMessage({ id: "".concat(MESSAGE_PREFIX, ".after.period") })),
                    React.createElement("th", null, formatMessage({ id: "".concat(MESSAGE_PREFIX, ".after.count") })),
                    React.createElement("th", null, formatMessage({ id: "".concat(MESSAGE_PREFIX, ".after.what") })))),
            React.createElement("tbody", null, rows.map(function (row) {
                var _a;
                return (React.createElement("tr", { key: (_a = row.volumeId) !== null && _a !== void 0 ? _a : "created-".concat(row.label) },
                    React.createElement("td", null, row.labelBefore && row.labelBefore !== row.label
                        ? "".concat(row.labelBefore, " \u2192 ").concat(row.label)
                        : row.label),
                    React.createElement("td", null, formatVolumeRange(row.startDate, row.endDate)),
                    React.createElement("td", null, row.issueCount),
                    React.createElement("td", null,
                        React.createElement("span", { className: row.role === "CREATED" ? css.createdMark : css.changedMark }, formatMessage({
                            id: "".concat(MESSAGE_PREFIX, ".after.role.").concat(row.role),
                        })),
                        offAnchorLabels.has(row.label) &&
                            ", ".concat(formatMessage({ id: "".concat(MESSAGE_PREFIX, ".after.offAnchor") })))));
            })))));
};
export var VolumeWindowPreview = function (_a) {
    var preview = _a.preview;
    var formatMessage = useIntl().formatMessage;
    var freed = preview.freedPeriods.length > 0;
    var anchorChange = preview.anchorChange;
    var offAnchorLabels = useMemo(function () {
        return new Set(preview.createdVolumes
            .filter(function (created) { return created.offAnchor; })
            .map(function (created) { return created.label; }));
    }, [preview.createdVolumes]);
    return (React.createElement("div", { className: css.preview },
        React.createElement("p", null, formatMessage({ id: "".concat(MESSAGE_PREFIX, ".newLabel") }, { volume: describeVolume(preview) })),
        (anchorChange === null || anchorChange === void 0 ? void 0 : anchorChange.adopted) && anchorChange.toDay && anchorChange.toMonth && (React.createElement("p", null, formatMessage({ id: "".concat(MESSAGE_PREFIX, ".anchorChange") }, {
            from: formatVolumeAnchor(formatMessage, anchorChange.fromDay, anchorChange.fromMonth),
            to: formatVolumeAnchor(formatMessage, anchorChange.toDay, anchorChange.toMonth),
        }))),
        freed && (React.createElement(AfterTable, { rows: preview.affectedVolumes, offAnchorLabels: offAnchorLabels })),
        !freed && preview.neighbours.length > 0 && (React.createElement("div", { className: css.section },
            React.createElement("strong", null, formatMessage({ id: "".concat(MESSAGE_PREFIX, ".neighbours") })),
            React.createElement("ul", { className: css.list }, preview.neighbours.map(function (neighbour) { return (React.createElement("li", { key: neighbour.volumeId }, formatMessage({ id: "".concat(MESSAGE_PREFIX, ".neighbourChange") }, {
                before: neighbour.labelBefore,
                volume: describeVolume(neighbour),
            }))); })))),
        React.createElement(IssueList, { headingId: "".concat(MESSAGE_PREFIX, ".moved"), issues: preview.movedIssues }),
        React.createElement(FilledList, { issues: preview.filledIssues }),
        React.createElement(RenumberedList, { issues: preview.renumbered }),
        preview.engagedChanged > 0 && (React.createElement(MessageBanner, { type: "warning" }, formatMessage({ id: "".concat(MESSAGE_PREFIX, ".engagedChanged") }, { count: preview.engagedChanged }))),
        React.createElement(IssueList, { headingId: "".concat(MESSAGE_PREFIX, ".orphaned"), issues: preview.orphaned, blocking: true }),
        React.createElement(IssueList, { headingId: "".concat(MESSAGE_PREFIX, ".collisions"), issues: preview.collisions, blocking: true }),
        preview.neighbours.length === 0 &&
            preview.movedIssues.length === 0 &&
            preview.renumbered.length === 0 &&
            preview.createdVolumes.length === 0 &&
            preview.filledIssues.length === 0 &&
            preview.applicable && (React.createElement("p", null, formatMessage({ id: "".concat(MESSAGE_PREFIX, ".noImpact") }))),
        !preview.applicable && (React.createElement(MessageBanner, { type: "error" }, formatMessage({ id: "".concat(MESSAGE_PREFIX, ".notApplicable") })))));
};
